import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getAudio, putAudio } from '@/services/session/audioSessionStore';
import { abrirMicrofone } from '@/services/voice/mics';
import { falanteDaCaptura } from '@/services/voice/mics/ativos';
import { LinhaDoTempo } from '@/services/voice/mics/falante';
import { guardarFalantes } from '@/services/voice/mics/falantesGravacao';

/**
 * Listener invocado quando uma gravação termina. Recebe o blob e o ID
 * do paciente que iniciou a gravação. Deve retornar `true` quando
 * consumir o blob (ou seja, quando ele é o destinatário correto). Se
 * nenhum listener retornar truthy, o RecordingProvider salva o blob
 * direto no IndexedDB do paciente originador (failsafe contra perda).
 */
type CompletionCallback = (blob: Blob, patientId: string | null) => boolean | void;

interface RecordingContextValue {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  /** ID do paciente que iniciou a gravação ativa, ou null. */
  recordingPatientId: string | null;
  /** Compartilhado com o visualizador do GlobalAudioRecorder. */
  analyser: AnalyserNode | null;
  start: (patientId: string, userId: string) => Promise<void>;
  stop: () => void;
  pauseResume: () => void;
  /**
   * Registra um listener que recebe o blob assim que a gravação termina.
   * Retorna função de unsubscribe. Listeners devem retornar `true` para
   * consumir o blob; caso contrário ele é salvo no IndexedDB do paciente
   * originador.
   */
  onComplete: (cb: CompletionCallback) => () => void;
}

const RecordingContext = createContext<RecordingContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export const RecordingProvider = ({ children }: ProviderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [recordingPatientId, setRecordingPatientId] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const completionListenersRef = useRef<Set<CompletionCallback>>(new Set());
  /**
   * Blobs gerados sem nenhum listener "consumir" (i.e., nenhum listener
   * retornou true). Mantemos junto o `patientId` do dono original para que
   * o próximo listener registrado (ex.: o paciente correto reaberto) saiba
   * se deve aceitar.
   */
  const pendingBlobsRef = useRef<Array<{ blob: Blob; patientId: string | null }>>([]);
  /**
   * Refs do paciente/usuário que iniciaram a gravação. Usados dentro do
   * `mr.onstop` (closure) para enviar o blob ao destino correto mesmo se
   * o usuário navegou para outro paciente nesse meio tempo.
   */
  const recordingPatientIdRef = useRef<string | null>(null);
  const recordingUserIdRef = useRef<string | null>(null);
  /** Relógio da linha do tempo de quem falou (spec 016, fase 2). */
  const falantesTimerRef = useRef<number | null>(null);

  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = window.setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const cleanupAudioGraph = () => {
    try {
      sourceRef.current?.disconnect();
    } catch {
      // ignore
    }
    sourceRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    setAnalyser(null);
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  /**
   * Persiste o blob direto no IndexedDB do paciente originador. Usado
   * como failsafe quando nenhum listener consumiu o blob (ex.: o médico
   * trocou de paciente durante a gravação).
   */
  const persistOrphanBlob = async (
    userId: string,
    patientId: string,
    blob: Blob,
  ): Promise<void> => {
    try {
      const existing = (await getAudio(userId, patientId, 'session')) ?? [];
      await putAudio(userId, patientId, 'session', [...existing, blob]);
    } catch (err) {
      console.error('[recording] failsafe IndexedDB save falhou', err);
    }
  };

  const start = useCallback(async (patientId: string, userId: string) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      return;
    }
    try {
      const stream = await abrirMicrofone(true, { uso: 'gravacao' });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];

      // Spec 016, fase 2: com a lapela "Médico" + microfone da sala, marca
      // quem fala a cada 100 ms do tempo GRAVADO (pausa não conta). Vai pra
      // IA como texto junto do áudio (ver falantesGravacao.ts).
      const linha = falanteDaCaptura(stream) !== undefined ? new LinhaDoTempo() : null;
      let gravado = 0;
      let ultimo = performance.now();
      if (falantesTimerRef.current) clearInterval(falantesTimerRef.current);
      falantesTimerRef.current = linha
        ? window.setInterval(() => {
            const agora = performance.now();
            if (mr.state === 'recording') {
              gravado += (agora - ultimo) / 1000;
              linha.marcar(gravado, falanteDaCaptura(stream) ?? null);
            }
            ultimo = agora;
          }, 100)
        : null;

      // Captura o paciente/usuário donos desta gravação.
      recordingPatientIdRef.current = patientId;
      recordingUserIdRef.current = userId;
      setRecordingPatientId(patientId);

      mr.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (linha) {
          if (falantesTimerRef.current) clearInterval(falantesTimerRef.current);
          falantesTimerRef.current = null;
          linha.fechar(gravado);
          guardarFalantes(blob, { duracao: linha.duracao, trechos: linha.trechos() });
        }
        const ownerPid = recordingPatientIdRef.current;
        const ownerUid = recordingUserIdRef.current;

        let consumed = false;
        if (completionListenersRef.current.size > 0) {
          completionListenersRef.current.forEach((cb) => {
            try {
              const result = cb(blob, ownerPid);
              if (result === true) consumed = true;
            } catch (err) {
              console.error('[recording] listener error', err);
            }
          });
        }

        if (!consumed) {
          // Nenhum listener aceitou — provável que o médico trocou de
          // paciente. Salva direto no IndexedDB do paciente originador.
          if (ownerPid && ownerUid) {
            void persistOrphanBlob(ownerUid, ownerPid, blob);
          } else {
            // Sem dono identificado — coloca em fila para o próximo listener.
            pendingBlobsRef.current.push({ blob, patientId: ownerPid });
          }
        }

        stopStream();
        recordingPatientIdRef.current = null;
        recordingUserIdRef.current = null;
        setRecordingPatientId(null);
      };

      mr.start(200);

      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ac = new AC();
      const an = ac.createAnalyser();
      an.fftSize = 256;
      const src = ac.createMediaStreamSource(stream);
      src.connect(an);
      audioContextRef.current = ac;
      sourceRef.current = src;
      setAnalyser(an);

      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      startTimer();
    } catch (err) {
      console.error('Erro ao acessar microfone', err);
      alert('Não foi possível acessar seu microfone. Verifique as permissões.');
    }
  }, []);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    }
    setIsRecording(false);
    setIsPaused(false);
    stopTimer();
    cleanupAudioGraph();
    mediaRecorderRef.current = null;
  }, []);

  const pauseResume = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    if (mr.state === 'paused') {
      mr.resume();
      setIsPaused(false);
      startTimer();
    } else if (mr.state === 'recording') {
      mr.pause();
      setIsPaused(true);
      stopTimer();
    }
  }, []);

  const onComplete = useCallback((cb: CompletionCallback) => {
    completionListenersRef.current.add(cb);
    if (pendingBlobsRef.current.length > 0) {
      const queue = pendingBlobsRef.current;
      pendingBlobsRef.current = [];
      const remaining: Array<{ blob: Blob; patientId: string | null }> = [];
      queue.forEach((item) => {
        try {
          const result = cb(item.blob, item.patientId);
          if (result !== true) {
            remaining.push(item);
          }
        } catch (err) {
          console.error('[recording] listener error', err);
          remaining.push(item);
        }
      });
      pendingBlobsRef.current = remaining;
    }
    return () => {
      completionListenersRef.current.delete(cb);
    };
  }, []);

  useEffect(() => {
    return () => {
      stopTimer();
      if (falantesTimerRef.current) clearInterval(falantesTimerRef.current);
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') {
        mr.stop();
      }
      stopStream();
      cleanupAudioGraph();
      mediaRecorderRef.current = null;
    };
  }, []);

  return (
    <RecordingContext.Provider
      value={{
        isRecording,
        isPaused,
        recordingTime,
        recordingPatientId,
        analyser,
        start,
        stop,
        pauseResume,
        onComplete,
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRecording = (): RecordingContextValue => {
  const ctx = useContext(RecordingContext);
  if (!ctx) {
    throw new Error('useRecording deve ser usado dentro de <RecordingProvider>');
  }
  return ctx;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalRecording = (): RecordingContextValue | null => {
  return useContext(RecordingContext);
};
